import { DialogContent, IconButton, Tooltip, } from "@mui/material";
import { JsonRouteMetric } from "./SegmentMetricTable";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { generateContent, getRouteInputs, handleEmail, handlePrint } from "./RouteUtils";
import {
    useAddTripMutation,
    useGetRouteMetadataQuery,
    useGetRouteMetricsAndInstructionsQuery,
    useSetSelectedRouteMutation,
} from "../../services/routing";

import { skipToken } from "@reduxjs/toolkit/query/react";
import { closeDrawer, DrawerStateKinds, openTripLibrary, useDrawerState } from "../../features/Drawer/drawerSlice";
import {
    filterDeselectedRoutes,
    setHasSelectedBackendRoute,
    setWilmaState,
    useHasSelectedBackendRoute,
    useRiskViewerStateKind,
    useSelectedRoute,
} from "../../features/riskViewer/riskViewerSlice";
import MobileNavBar from "../tripSidebar/MobileNavBar";
import { faEnvelope, faPrint } from "@fortawesome/sharp-duotone-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import { useDispatch } from "react-redux";
import { setSelectedTime } from "../../features/surface/surfaceSlice";
import { editTrip, getProcessingRequestStatus } from "../trips/tripUtils";
import useIdentity from "../../app/auth";
import AdminControls from "./AdminControls";
import TripHeader from "./TripHeader";
import { generateMultistartRouteRequestInput } from "../trips/form/TripFormTypeConversions";
import { SelectRouteButton, TakeRouteButton } from "../MultistartSelectionDrawer/MutistartDrawerStyles";
import { SEE_MULTISTART_PROMPT, SEE_SUREROUTE_PROMPT, SELECT_SUREROUTE_RESULT } from "../UserVisibleStrings";
import MultistartCard from "../MultistartSelectionDrawer/MultistartCard";
import { useUserViewOverride } from "../../features/riskViewer/userViewOverrideSlice";
import { ComputationStatus, RouteRequestInputType, RouteRequestState } from "../../generated/gpb/api/route";

enum OverviewMode
    {
    SelectedRoute,
    SureRoute,
    Advisory
}

export default function RouteOverview( )
{
    const [ selectedTab, setSelectedTab ] = useState( 0 );
    const drawerState = useDrawerState();
    const tripData = drawerState.kind === DrawerStateKinds.TripResults ? drawerState.trip : undefined;
    const routeId = drawerState.kind === DrawerStateKinds.TripResults ? drawerState.routeId : undefined;
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const riskViewerStateKind = useRiskViewerStateKind();
    const selectedRoute = useSelectedRoute();
    const selectedRouteResult = tripData?.routeResult.find( result => result.routeId === selectedRoute );
    const tripId = tripData?.Id;
    const userViewOverride = useUserViewOverride();
    const { isAdmin } = useIdentity( userViewOverride );
    const [ addTrip ] = useAddTripMutation();
    const routeIsErrored = getProcessingRequestStatus( tripData ) === ComputationStatus.ERROR;
    const [ shouldDisableSendMultistart, setShouldDisableSendMultistart ] = useState<boolean>( false );
    const hasSelectedBackendRoute: boolean = useHasSelectedBackendRoute();
    const isSubmittingRef = useRef( false );

    const {
        isUninitialized,
        isLoading,
        data: metricsAndInstructions,
        refetch: refetchRouteSegmentsQuery,
    } = useGetRouteMetricsAndInstructionsQuery( routeId?.hexString ?? skipToken );
    useEffect( () =>
    {
        if ( !isUninitialized )
        {
            refetchRouteSegmentsQuery().catch( ( err ) => console.error( "Request failed " + err ) );
        }
    }, [ routeId, isUninitialized ] );

    const { data: routeMetadata } = useGetRouteMetadataQuery(
        routeId?.hexString ?? skipToken
    );

    const requestTableData: Array<JsonRouteMetric> = useMemo( () =>
    {
        return getRouteInputs(
            tripData,
            routeMetadata?.forecastTime
        ); // forecast time
    }, [ tripData, routeMetadata ] );
    const [ openHelp, setOpenHelp ] = useState( false );
    const [ chooseRoute ] = useSetSelectedRouteMutation();
    let overviewMode: OverviewMode = OverviewMode.SelectedRoute;

    if (
        tripData?.routeState === RouteRequestState.MULTISTART_ELIGIBLE &&
        tripData?.routeRequestInput?.requestType === RouteRequestInputType.ADVISORY &&
        !hasSelectedBackendRoute
    )
    {
        overviewMode = OverviewMode.Advisory;
    }
    else if (
        tripData?.routeState === RouteRequestState.MULTISTART_ELIGIBLE &&
        tripData?.routeRequestInput?.requestType !== RouteRequestInputType.ADVISORY &&
        !hasSelectedBackendRoute
    )
    {
        overviewMode = OverviewMode.SureRoute;
    }
    const content = generateContent(
        isLoading, routeIsErrored, metricsAndInstructions, selectedTab, setSelectedTab, requestTableData, tripData, openHelp, setOpenHelp, overviewMode !== OverviewMode.SelectedRoute
    );

    function handleEditClick()
    {
        editTrip( dispatch, tripData );
    }

    const handleMultistartClick = async () =>
    {
        if ( isSubmittingRef.current ) return;

        try
        {
            if ( tripId !== undefined && tripData?.name !== undefined && tripData?.routeRequestInput !== undefined )
            {
                isSubmittingRef.current = true;
                setShouldDisableSendMultistart( true );
                const newRouteRequest = generateMultistartRouteRequestInput( tripData.routeRequestInput );
                await addTrip( {
                    parentTripId: tripId,
                    tripName: tripData?.name,
                    routeRequestInput: newRouteRequest,
                } );
                dispatch( setWilmaState() );
                dispatch( openTripLibrary() );
            }
        }
        catch ( error )
        {
            console.error( "Error adding request:", error );
        }
    };

    const handleAdvisoryClick = () =>
    {
        editTrip( dispatch, tripData, true );
    };

    const handleSelectRouteClick = async () =>
    {
        if ( isSubmittingRef.current ) return;

        try
        {
            if ( tripId !== undefined && routeId !== undefined )
            {
                isSubmittingRef.current = true;
                setShouldDisableSendMultistart( true );
                dispatch( setHasSelectedBackendRoute( ) );
                dispatch( filterDeselectedRoutes( routeId ) );
                await chooseRoute( { tripId: tripId?.hexString, routeId: routeId?.hexString } );
            }
        }
        catch ( error )
        {
            console.error( "Error adding request:", error );
        }
    };
    return (
        <div
            style = {{
                display: "flex",
                flexDirection: "column",
                height: "100dvh",
                overflow: "auto"
            }}
        >
            <TripHeader
                tripData = {tripData}
                isAdmin = {isAdmin}
                isMissingRoutes = {false} //Should probably be removed, but we may want to duplicate this functionality somehow
                handleEditClick = {handleEditClick}
                dispatch = {dispatch}
                riskViewerStateKind = {riskViewerStateKind}
            />
            <div style = {{ flex: 1, overflowY: "auto" }}>
                <DialogContent sx = {{ padding: selectedTab === 1 ? "0px" : "" }}>
                    {/*getWaypointList( routeRequestAndResult?.routeRequestInput?.waypoints )*/}

                    { isAdmin && tripData !== undefined ? ( <AdminControls/> ) : undefined }
                    {content}
                </DialogContent>


                {overviewMode === OverviewMode.SureRoute && selectedRouteResult !== undefined && (
                    <div>
                        <MultistartCard
                            result = {selectedRouteResult}
                            isSelected
                            showButtons = {false}
                            label = {undefined}
                        />

                        {!shouldDisableSendMultistart && (
                            <div style = {{ paddingLeft: "10px", paddingRight: "10px" }}>
                                <TakeRouteButton onClick = {handleMultistartClick}>
                                    {SEE_MULTISTART_PROMPT}
                                </TakeRouteButton>
                                <SelectRouteButton onClick = {handleSelectRouteClick}>
                                    {SELECT_SUREROUTE_RESULT}
                                </SelectRouteButton>
                            </div>
                        )}
                    </div>
                )}

                {overviewMode === OverviewMode.Advisory && selectedRouteResult !== undefined && (
                    <div>
                        <MultistartCard
                            result = {selectedRouteResult}
                            isSelected
                            showButtons = {false}
                            label = {undefined}
                        />

                        {!shouldDisableSendMultistart && (
                            <div style = {{ paddingLeft: "10px", paddingRight: "10px" }}>
                                <TakeRouteButton onClick = {handleAdvisoryClick}>
                                    {SEE_SUREROUTE_PROMPT}
                                </TakeRouteButton>
                                <SelectRouteButton onClick = {handleSelectRouteClick}>
                                    {SELECT_SUREROUTE_RESULT}
                                </SelectRouteButton>
                            </div>
                        )}
                    </div>
                )}


                {overviewMode === OverviewMode.SelectedRoute && selectedTab === 0 && !routeIsErrored && (
                    <div>
                        <Tooltip title = "Print">
                            <IconButton onClick = {() => handlePrint()}>
                                <FontAwesomeIcon icon = {faPrint} size = "sm" fixedWidth/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title = "Email">
                            <IconButton onClick = {() => handleEmail( metricsAndInstructions, tripData )}>
                                <FontAwesomeIcon icon = {faEnvelope} size = "sm" fixedWidth/>
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
            </div>

            {isMobile &&
                <MobileNavBar
                    onClose = {() =>
                    {
                        dispatch( setWilmaState( ) );
                        dispatch( setSelectedTime( Date.now() ) );
                        dispatch( closeDrawer() );
                    }}
                />}
        </div>
    );
}
